import React, { useState, useMemo } from 'react';
import { Puzzle, Challenge, AppSettings } from '../types';
import { BIBLE_BOOKS, COBALT_BLUE } from '../constants';
import { ArrowLeft, BookOpen, CheckCircle, XCircle, Binary, Info, KeyRound, Swords } from 'lucide-react';
import { playCorrect, playWrong, triggerHaptic } from '../utils/feedback';

interface VerseUnlockProps {
  puzzle: Puzzle;
  challenge?: Challenge | null;
  settings: AppSettings;
  onSuccess: () => void;
  onBack: () => void;
}

const VerseUnlock: React.FC<VerseUnlockProps> = ({ puzzle, challenge, settings, onSuccess, onBack }) => {
  const [selectedBook, setSelectedBook] = useState('Genesis');
  const [chapter, setChapter] = useState('1');
  const [verse, setVerse] = useState('1');
  const [feedback, setFeedback] = useState<'NONE' | 'SUCCESS' | 'ERROR'>('NONE');
  const [showCipher, setShowCipher] = useState(false);

  // Challenge logic overrides defaults
  const targetBook = challenge ? challenge.targetBook : puzzle.book;
  const targetChapter = challenge ? challenge.targetChapter : puzzle.chapter;
  const targetVerse = challenge ? challenge.targetVerse : puzzle.verse;
  const questionText = challenge ? challenge.customQuestion : puzzle.accessverseText;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const isCorrect = 
      selectedBook === targetBook && 
      parseInt(chapter) === targetChapter && 
      parseInt(verse) === targetVerse;

    if (isCorrect) {
      setFeedback('SUCCESS');
      playCorrect(settings);
      triggerHaptic(settings, 'success');
      setTimeout(() => {
        onSuccess();
      }, 1000);
    } else {
      setFeedback('ERROR');
      playWrong(settings);
      triggerHaptic(settings, 'error');
      setTimeout(() => setFeedback('NONE'), 2000);
    }
  };

  const codedHint = useMemo(() => {
    const offsets = [-2, -1, 1, 2];
    // Use a stable offset based on puzzle ID
    const seed = puzzle.id.split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
    const offset = offsets[seed % offsets.length];
    
    const applyOffset = (text: string, shift: number) => {
      // First, shift all letters individually
      let shiftedText = text.split('').map(char => {
        if (/[a-zA-Z]/.test(char)) {
          const isUpper = char === char.toUpperCase();
          const base = isUpper ? 65 : 97;
          let code = char.charCodeAt(0) - base + shift;
          if (code < 0) code += 26;
          if (code > 25) code -= 26;
          return String.fromCharCode(base + code);
        }
        return char;
      }).join('');

      // Then, find all numbers and shift them as whole values
      return shiftedText.replace(/\d+/g, (match) => {
        const num = parseInt(match) + shift;
        return num.toString();
      });
    };

    const bookFormatted = targetBook.replace(/^(\d)\s/, '$1-');
    const rawCode = `${bookFormatted} ${targetChapter}:${targetVerse}`;
    const encoded = applyOffset(rawCode, offset);
    
    const hintText = offset === 1 ? "Replace with alphabets and numbers 1 step back" :
                     offset === 2 ? "Replace with alphabets and numbers 2 steps back" :
                     offset === -1 ? "Replace with alphabets and numbers 1 step forward" :
                     "Replace with alphabets and numbers 2 steps forward";

    return {
      code: encoded,
      hint: hintText
    };
  }, [targetBook, targetChapter, targetVerse, puzzle.id]);

  return (
    <div className="flex-1 p-4 md:p-6 flex flex-col items-center bg-gray-50 overflow-y-auto pt-safe">
      <div className="max-w-2xl w-full py-12">
        <button 
          onClick={onBack}
          className="mb-6 flex items-center gap-2 text-blue-600 font-bold hover:text-blue-800 transition px-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="bg-white rounded-[2.5rem] shadow-2xl p-6 md:p-12 border border-gray-100">
          <div className="flex items-center justify-center mb-8">
            <div className={`p-4 rounded-full border-4 border-white shadow-md ${challenge ? 'bg-rose-50' : 'bg-blue-50'}`}>
              {challenge ? <Swords className="w-10 h-10 text-rose-600" /> : <BookOpen className="w-10 h-10 text-blue-700" />}
            </div>
          </div>

          <h2 className="text-center text-[10px] uppercase tracking-[0.3em] font-bold mb-3" style={{ color: challenge ? '#e11d48' : COBALT_BLUE }}>
            {challenge ? `Challenge from ${challenge.challengerName}` : 'Scripture Quiz'}
          </h2>
          <h1 className="text-center text-3xl font-cinzel font-bold text-gray-900 mb-8 leading-tight">
            {challenge ? 'Answer to Enter' : 'Identify the Source'}
          </h1>

          {/* Remove overflow-hidden here to show the floating label correctly */}
          <div className={`relative mb-8 p-6 md:p-10 rounded-3xl border shadow-inner ${challenge ? 'bg-rose-50 border-rose-100' : 'bg-amber-50 border-amber-100'}`}>
             <div className={`absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-5 py-1.5 rounded-full text-[10px] uppercase font-bold tracking-widest shadow-md z-20 ${challenge ? 'bg-rose-200 text-rose-800' : 'bg-amber-200 text-red-700'}`}>
               {challenge ? 'Question' : 'Scripture'}
             </div>
             
             <p className="text-xl md:text-2xl italic text-gray-700 leading-relaxed text-center font-serif relative z-10">
               "{questionText}"
             </p>
             
             <div className="mt-8 flex flex-col items-center gap-3">
                {!showCipher ? (
                  <button 
                    onClick={() => setShowCipher(true)}
                    className="flex items-center gap-2 text-amber-700 bg-amber-200/50 hover:bg-amber-200 px-5 py-2 rounded-full text-xs font-bold transition-all active:scale-95 shadow-sm"
                  >
                    <Binary className="w-4 h-4" />
                    Coded Hint
                  </button>
                ) : (
                  <div className="mt-4 p-5 bg-white/90 backdrop-blur-sm rounded-2xl border border-amber-200 shadow-sm animate-in fade-in slide-in-from-top-2 duration-500 w-full max-w-md">
                    <div className="flex flex-col items-center text-center">
                      <div className="flex justify-between items-center w-full mb-4">
                         <div className="flex items-center gap-2">
                           <KeyRound className="w-4 h-4 text-amber-700" />
                           <p className="text-[10px] font-bold text-amber-800 uppercase tracking-[0.2em]">Coded Hint</p>
                         </div>
                         <button onClick={() => setShowCipher(false)} className="text-[10px] text-amber-400 hover:text-amber-600 font-bold uppercase tracking-tighter underline">Hide</button>
                      </div>
                      
                      <div className="w-full py-6 bg-amber-50/50 rounded-xl border border-amber-100 mb-3">
                        <p className="text-2xl font-mono font-bold text-amber-900 tracking-widest">
                          {codedHint.code}
                        </p>
                      </div>
                      
                      <p className="text-[11px] font-medium text-amber-700 italic">
                        ({codedHint.hint})
                      </p>
                    </div>
                  </div>
                )}
             </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase text-gray-400 tracking-wider ml-1">Bible Book</label>
                <select className="w-full bg-gray-50 border border-gray-200 p-4 rounded-2xl focus:ring-2 outline-none appearance-none cursor-pointer font-bold text-gray-700 shadow-sm"
                  value={selectedBook} onChange={(e) => setSelectedBook(e.target.value)}>
                  {BIBLE_BOOKS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase text-gray-400 tracking-wider ml-1">Chapter</label>
                <input type="number" min="1" className="w-full bg-gray-50 border border-gray-200 p-4 rounded-2xl outline-none font-bold text-gray-700"
                  value={chapter} onChange={(e) => setChapter(e.target.value)} />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase text-gray-400 tracking-wider ml-1">Verse</label>
                <input type="number" min="1" className="w-full bg-gray-50 border border-gray-200 p-4 rounded-2xl outline-none font-bold text-gray-700"
                  value={verse} onChange={(e) => setVerse(e.target.value)} />
              </div>
            </div>

            <button type="submit" disabled={feedback === 'SUCCESS'}
              className={`w-full py-5 rounded-2xl font-bold text-xl transition-all shadow-xl flex items-center justify-center gap-3 active:scale-95
                ${feedback === 'SUCCESS' ? 'bg-green-600 text-white' : 
                  feedback === 'ERROR' ? 'bg-red-600 text-white' : (challenge ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'bg-blue-700 hover:bg-blue-800 text-white')}
              `}
            >
              {feedback === 'SUCCESS' ? 'Hallelujah!' : 
               feedback === 'ERROR' ? 'Try Again' : 
               (challenge ? 'Unlock Duel' : 'Unlock Puzzle')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VerseUnlock;